import type {
    AbstractTypename,
    AnyObjectType,
    ObjectTypename,
    OutputTypename,
} from '../derived';
import {
    couldBePossibleType,
    type ExtractPossibleType,
    isAbstractTypename,
    isPossibleType,
} from './abstract';
import { couldBeType, type ExtractTypename, isType } from './objects';

export type ExtractOutput<
    T,
    K extends OutputTypename
> = K extends AbstractTypename
    ? ExtractPossibleType<T, K>
    : K extends ObjectTypename
    ? ExtractTypename<T, K>
    : never;

/**
 * Runtime typeguard to check if the provided value has a typename.
 *
 * @param value The value to check.
 * @param typename The typename to check.
 */
export function isA<T extends AnyObjectType, K extends OutputTypename>(
    value: T | null | undefined,
    typename: K
): value is ExtractOutput<T, K> {
    if (value === null || value === undefined) return false;
    return isAbstractTypename(typename)
        ? isPossibleType(value, typename)
        : isType(value, typename);
}

/**
 * Runtime typeguard to check if a value could become a GraphQL type with the specified typename.
 *
 * @param value
 * @param typename
 */
export function couldBeA<T extends AnyObjectType, K extends OutputTypename>(
    value: T | null | undefined,
    typename: K
): value is ExtractOutput<T, K> | null | undefined {
    return isAbstractTypename(typename)
        ? couldBePossibleType(value, typename)
        : couldBeType(value, typename);
}

/**
 * Returns the value iff it is a GraphQL-object with the provided typename.
 *
 * @param value
 * @param typename
 */
export function asA<T extends AnyObjectType, K extends OutputTypename>(
    value: T | null | undefined,
    typename: K
): ExtractOutput<T, K> | undefined {
    if (isA(value, typename)) return value;
    return undefined;
}
